import React, { useCallback, useEffect, useState } from 'react';
import { FaBars, FaTimes, FaGithub, FaLinkedinIn, FaEnvelope, FaFileAlt } from 'react-icons/fa';
import resume from './assets/Adiba_Akter_resume.pdf';
import './styles/Navigation.css';

const NAV_LINKS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'research', label: 'Research' },
  { id: 'leadership', label: 'Leadership' },
  { id: 'awards', label: 'Awards' },
  { id: 'contact', label: 'Contact' },
];

export const SOCIALS = {
  github: 'https://github.com/AdibaAdi',
  linkedin: 'https://www.linkedin.com/in/adiba-akter-9b025222b/',
  email: 'adiba.akter.98@gmail.com',
};

function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Scroll-spy: highlight the section currently in view */
  useEffect(() => {
    const sections = NAV_LINKS.map((link) => document.getElementById(link.id)).filter(
      Boolean
    );
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  /* Lock body scroll while the mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const handleNavClick = useCallback((event, id) => {
    event.preventDefault();
    setMenuOpen(false);
    const target = document.getElementById(id);
    if (!target) return;
    /* release the drawer's scroll lock now rather than a render later, so the
       scroll that follows is never swallowed on mobile */
    document.body.style.overflow = '';
    /* smooth only when the visitor has not asked for reduced motion */
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, []);

  return (
    <header className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
      <div className="nav-inner">
        <a
          className="nav-brand"
          href="#home"
          onClick={(event) => handleNavClick(event, 'home')}
          aria-label="Adiba Akter, back to top"
        >
          <span className="nav-brand-mark" aria-hidden="true">
            AA
          </span>
          <span className="nav-brand-name">Adiba Akter</span>
        </a>

        <nav
          id="primary-navigation"
          className={`nav-links ${menuOpen ? 'nav-links--open' : ''}`}
          aria-label="Primary"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`nav-link ${activeId === link.id ? 'nav-link--active' : ''}`}
              aria-current={activeId === link.id ? 'true' : undefined}
              onClick={(event) => handleNavClick(event, link.id)}
            >
              {link.label}
            </a>
          ))}

          <a
            className="nav-resume nav-resume--mobile"
            href={resume}
            target="_blank"
            rel="noreferrer"
            onClick={() => setMenuOpen(false)}
          >
            <FaFileAlt aria-hidden="true" /> View Résumé
          </a>

          <div className="nav-socials nav-socials--mobile">
            <a
              className="nav-social-btn"
              href={SOCIALS.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub profile"
            >
              <FaGithub aria-hidden="true" />
            </a>
            <a
              className="nav-social-btn"
              href={SOCIALS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn profile"
            >
              <FaLinkedinIn aria-hidden="true" />
            </a>
            <a
              className="nav-social-btn"
              href={`mailto:${SOCIALS.email}`}
              aria-label="Email Adiba"
            >
              <FaEnvelope aria-hidden="true" />
            </a>
          </div>
        </nav>

        <div className="nav-right">
          <div className="nav-socials">
            <a
              className="nav-social-btn"
              href={SOCIALS.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub profile"
              title="GitHub"
            >
              <FaGithub aria-hidden="true" />
            </a>
            <a
              className="nav-social-btn"
              href={SOCIALS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn profile"
              title="LinkedIn"
            >
              <FaLinkedinIn aria-hidden="true" />
            </a>
            <a
              className="nav-social-btn"
              href={`mailto:${SOCIALS.email}`}
              aria-label="Email Adiba"
              title="Email"
            >
              <FaEnvelope aria-hidden="true" />
            </a>
            <a
              className="nav-social-btn nav-social-btn--resume"
              href={resume}
              target="_blank"
              rel="noreferrer"
              aria-label="View résumé (PDF)"
              title="Résumé"
            >
              <FaFileAlt aria-hidden="true" />
            </a>
          </div>

          <button
            type="button"
            className="nav-toggle"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <button
          type="button"
          className="nav-scrim"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </header>
  );
}

export default Navigation;
